import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import DashboardPage from "@/pages/DashboardPage";
import DailyEntryPage from "@/pages/DailyEntryPage";
import HistoryPage from "@/pages/HistoryPage";
import AdminPage from "@/pages/AdminPage";
import ProfilePage from "@/pages/ProfilePage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ForceChangePasswordPage from "@/pages/ForceChangePasswordPage";
import MonthlyPage from "@/pages/MonthlyPage";
import TasksPage from "@/pages/TasksPage";
import TaskHistoryPage from "@/pages/TaskHistoryPage";
import TimelinePage from "@/pages/TimelinePage";
import PostTaskPage from "@/pages/PostTaskPage";
import CommitmentsPage from "@/pages/CommitmentsPage";
import TaskAuthPage from "@/pages/TaskAuthPage";
import TaskListPage from "@/pages/TaskListPage";
import TaskTimelinePage from "@/pages/TaskTimelinePage";
import MyPage from "@/pages/MyPage";
import AppShell from "@/components/AppShell";
import { deviceType, isPageRestricted } from "@/lib/device";

function Protected({ children, adminOnly = false, module = null, bare = false }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen grid place-items-center bg-slate-950 text-slate-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password && loc.pathname !== "/force-change-password") return <Navigate to="/force-change-password" replace />;
  if (adminOnly && user.role !== "admin" && !user.admin_level) return <Navigate to="/" replace />;
  if (module && user.role !== "admin" && !user.admin_level
      && isPageRestricted(user, module, deviceType())) {
    return <Navigate to={module === "dashboard" ? "/fatigue" : "/"} replace />;
  }
  if (bare) return children;
  return <AppShell>{children}</AppShell>;
}

function ThemeSync() {
  const { user } = useAuth();
  useEffect(() => {
    const root = document.documentElement;
    const apply = () => {
      const pref = user?.theme || "system";
      const eff = pref === "system"
        ? (window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark")
        : pref;
      root.classList.toggle("light", eff === "light");
      root.classList.toggle("dark", eff === "dark");
    };
    apply();
    if (!user?.theme || user.theme === "system") {
      const mq = window.matchMedia("(prefers-color-scheme: light)");
      mq.addEventListener("change", apply);
      return () => mq.removeEventListener("change", apply);
    }
  }, [user?.theme]);
  return null;
}

function AuthCallback() {
  const { googleSession } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sid = new URLSearchParams(loc.hash.replace(/^#/, "")).get("session_id");
    (async () => {
      try {
        if (!sid) throw new Error("Missing session_id");
        const u = await googleSession(sid);
        window.history.replaceState(null, "", window.location.pathname);
        nav(u.role === "admin" || u.admin_level ? "/admin" : "/", { replace: true });
      } catch (err) {
        const detail = err.response?.data?.detail;
        nav("/login", { replace: true, state: { error: typeof detail === "string" ? detail : "Google sign-in failed" } });
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen grid place-items-center bg-slate-950 text-slate-400" data-testid="auth-callback">
      <div className="text-center">
        <div className="font-heading text-lg">Completing Google sign-in…</div>
      </div>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  // Read the fragment via useLocation (reactive) — NOT window.location.hash — so the
  // session_id exchange runs before any route/auth check.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/" element={<Protected module="dashboard"><DashboardPage /></Protected>} />
      <Route path="/entry" element={<Navigate to="/fatigue" replace />} />
      <Route path="/fatigue" element={<Protected><DailyEntryPage /></Protected>} />
      <Route path="/tasks" element={<Protected module="tasks"><TasksPage /></Protected>} />
      <Route path="/tasks/list" element={<Protected module="tasks"><TaskListPage /></Protected>} />
      <Route path="/task-timeline" element={<Protected module="tasks"><TaskTimelinePage /></Protected>} />
      <Route path="/timeline" element={<Protected module="tasks" bare><TimelinePage /></Protected>} />
      <Route path="/post-task" element={<Protected module="tasks"><PostTaskPage /></Protected>} />
      <Route path="/commitments" element={<Protected module="tasks"><CommitmentsPage /></Protected>} />
      <Route path="/task-auth" element={<Protected module="tasks"><TaskAuthPage /></Protected>} />
      <Route path="/my" element={<Protected><MyPage /></Protected>} />
      <Route path="/task-history" element={<Protected module="task_history"><TaskHistoryPage /></Protected>} />
      <Route path="/history" element={<Protected module="history"><HistoryPage /></Protected>} />
      <Route path="/monthly" element={<Protected module="monthly"><MonthlyPage /></Protected>} />
      <Route path="/profile" element={<Protected><ProfilePage /></Protected>} />
      <Route path="/force-change-password" element={<Protected><ForceChangePasswordPage /></Protected>} />
      <Route path="/admin" element={<Protected adminOnly><AdminPage /></Protected>} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-slate-950 text-slate-100">
      <BrowserRouter>
        <AuthProvider>
          <ThemeSync />
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
export default App;
